import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Award, Clock, AlertCircle, ArrowLeft, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import Header from "../components/Header";

/** Student quiz player — renders a teacher-built quiz, collects answers,
 * submits, then shows an auto-graded summary. Short-answer questions are
 * flagged as pending manual review. */
export default function TakeQuiz() {
  const { assignmentId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authLoading && !user) navigate("/");
  }, [authLoading, user, navigate]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [q, s] = await Promise.all([
          api.get(`/me/assignments/${assignmentId}/quiz`),
          api.get(`/me/assignments/${assignmentId}/submission`).catch(() => ({ data: { submission: null } })),
        ]);
        if (cancel) return;
        setQuiz(q.data);
        setSubmission(s.data.submission);
        if (s.data.submission?.answers) setAnswers(s.data.submission.answers);
      } catch (e) {
        setError(e?.response?.data?.detail || "Failed to load quiz");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [assignmentId]);

  const answered = Object.keys(answers).length;
  const total = quiz?.questions?.length || 0;

  const handleSubmit = async () => {
    if (answered < total && !window.confirm(`You've answered ${answered}/${total}. Submit anyway?`)) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post(`/me/assignments/${assignmentId}/quiz-attempt`, { answers });
      // Reload submission to show summary.
      const s = await api.get(`/me/assignments/${assignmentId}/submission`);
      setSubmission(s.data.submission);
      // Scroll to top
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(e?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || authLoading) {
    return <div className="min-h-screen bg-bone flex items-center justify-center text-sage">Loading…</div>;
  }
  if (error && !quiz) {
    return (
      <div className="min-h-screen bg-bone">
        <Header />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <div className="text-terracotta text-sm">{error}</div>
          <button onClick={() => navigate("/dashboard")} className="mt-4 text-sage hover:underline text-sm">← Back to dashboard</button>
        </div>
      </div>
    );
  }

  const showResults = submission && submission.status !== undefined;

  return (
    <div className="min-h-screen bg-bone">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 pb-24">
        <button onClick={() => navigate("/dashboard")} className="text-xs text-muted-foreground hover:text-sage inline-flex items-center gap-1 mb-4" data-testid="take-quiz-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to dashboard
        </button>
        <div className="mb-6">
          <div className="text-xs uppercase tracking-[0.2em] text-terracotta font-bold">Teacher quiz</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1" data-testid="take-quiz-title">{quiz.title}</h1>
          {quiz.description && <p className="text-sm text-muted-foreground mt-2">{quiz.description}</p>}
        </div>

        {showResults && <ResultsCard submission={submission} />}

        <div className="space-y-4">
          {quiz.questions.map((q, i) => (
            <QuestionCard
              key={q.question_id}
              index={i}
              q={q}
              answer={answers[q.question_id]}
              onChange={(val) => setAnswers({ ...answers, [q.question_id]: val })}
              locked={!!showResults}
            />
          ))}
        </div>

        {!showResults && (
          <div className="fixed bottom-0 inset-x-0 bg-white border-t border-border px-4 py-3 flex items-center gap-3 shadow-lg">
            <div className="flex-1 text-sm">
              <div className="text-xs text-muted-foreground">Progress</div>
              <div className="flex items-center gap-2">
                <div className="w-32 h-1.5 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full bg-sage" style={{ width: `${total ? (answered / total) * 100 : 0}%` }} />
                </div>
                <span className="font-semibold">{answered}/{total}</span>
              </div>
            </div>
            {error && <div className="text-xs text-terracotta">{error}</div>}
            <button
              onClick={handleSubmit}
              disabled={submitting || total === 0}
              data-testid="take-quiz-submit"
              className="bg-sage hover:bg-sage-dark disabled:opacity-60 text-white font-medium px-6 py-2.5 rounded-xl transition-all"
            >
              {submitting ? "Submitting…" : "Submit quiz"}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function ResultsCard({ submission }) {
  return (
    <div className="mb-6 rounded-2xl border border-sage/30 bg-gradient-to-br from-sage/5 via-white to-terracotta/5 p-5 sm:p-6" data-testid="take-quiz-results">
      <div className="flex items-center gap-4 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-sage text-white flex items-center justify-center">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.2em] text-sage font-bold">Result</div>
          <div className="font-display text-2xl font-semibold tracking-tight">
            {submission.percentage != null ? `${submission.percentage}%` : "—"}
            <span className="text-muted-foreground text-sm font-normal ms-2">
              · {submission.auto_score}/{submission.auto_total} auto-graded
            </span>
          </div>
          {submission.requires_manual && submission.status !== "reviewed" && (
            <div className="text-xs text-amber-700 font-medium inline-flex items-center gap-1 mt-1">
              <AlertCircle className="w-3.5 h-3.5" /> Short-answer questions await teacher review.
            </div>
          )}
          {submission.status === "reviewed" && (
            <div className="text-xs text-sage font-medium inline-flex items-center gap-1 mt-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Reviewed by teacher
            </div>
          )}
          {submission.feedback && (
            <div className="mt-3 text-sm bg-white border border-border rounded-lg p-3" data-testid="take-quiz-feedback">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-1">Teacher's feedback</div>
              {submission.feedback}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function QuestionCard({ index, q, answer, onChange, locked }) {
  const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return (
    <div className="bg-white border border-border rounded-2xl p-5 space-y-3" data-testid={`take-quiz-q-${index}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-bold text-muted-foreground">Q{index + 1}</span>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">
          {q.type === "mcq" ? "MCQ" : q.type === "truefalse" ? "T / F" : "Short answer"}
        </span>
      </div>
      <div className="font-medium leading-relaxed">{q.prompt}</div>
      {q.image_file_id && (
        <img src={`${apiBase}/api/files/${q.image_file_id}`} alt="" className="max-h-56 rounded-lg border border-border" />
      )}
      {q.type === "mcq" && (
        <div className="space-y-2">
          {q.options.map((opt, i) => (
            <label key={`${q.question_id}-opt-${i}`} className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition ${
              answer === i ? "bg-sage/10 border-sage" : "bg-bone border-border hover:border-sage/50"
            } ${locked ? "opacity-70 pointer-events-none" : ""}`}>
              <input type="radio" name={`q-${q.question_id}`} checked={answer === i}
                onChange={() => onChange(i)} data-testid={`take-quiz-q-${index}-opt-${i}`}
                className="w-4 h-4 accent-sage" />
              <span className="text-sm">
                <strong className="me-1">{String.fromCharCode(65 + i)}.</strong>{opt}
              </span>
            </label>
          ))}
        </div>
      )}
      {q.type === "truefalse" && (
        <div className="flex items-center gap-2">
          {[true, false].map((v) => (
            <button
              key={String(v)}
              disabled={locked}
              onClick={() => onChange(v)}
              data-testid={`take-quiz-q-${index}-${v ? "true" : "false"}`}
              className={`px-5 py-2 rounded-lg border transition text-sm font-medium ${
                answer === v ? "bg-sage text-white border-sage" : "bg-bone border-border hover:border-sage"
              }`}
            >
              {v ? "True" : "False"}
            </button>
          ))}
        </div>
      )}
      {q.type === "short" && (
        <textarea
          value={answer || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={locked}
          placeholder="Your answer…"
          data-testid={`take-quiz-q-${index}-short`}
          className="w-full px-3 py-2 rounded-lg border border-border bg-bone text-sm focus:outline-none focus:border-sage min-h-[60px]"
        />
      )}
    </div>
  );
}
